// frontend/src/pages/ConfirmarEmailPage/ConfirmarEmailPage.jsx
// Página acessada pelo link do e-mail de confirmação (cadastro "Criar conta").
// Confirmou: o backend já devolve os tokens e a pessoa entra direto no app.

import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Image, Spin, Alert } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmarEmail } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { irAposLogin } from '../../utils/legalAccess';
import ReenviarConfirmacao from '../../components/ReenviarConfirmacao/ReenviarConfirmacao';
import logo from '../../assets/images/logo_vertical.jpg';
import '../../styles/auth.css';

const { Title, Text } = Typography;

// O link é de uso único: no StrictMode (dev) o efeito roda duas vezes, então a
// mesma chave reaproveita a mesma requisição em vez de confirmar de novo (404).
const confirmacoes = new Map();
function confirmarUmaVez(key) {
  if (!confirmacoes.has(key)) confirmacoes.set(key, confirmarEmail(key));
  return confirmacoes.get(key);
}

const ConfirmarEmailPage = () => {
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [emailConfirmado, setEmailConfirmado] = useState('');
  const navigate = useNavigate();
  const { entrarComTokens } = useAuth();

  useEffect(() => {
    if (!key) {
      setStatus('error');
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const data = await confirmarUmaVez(key);
        if (cancelled) return;
        setEmailConfirmado(data?.email || '');
        if (data?.access) {
          try {
            const userData = await entrarComTokens(data.access, data.refresh);
            if (!cancelled) irAposLogin(userData, navigate);
            return;
          } catch {
            // Sem sessão: cai na tela de sucesso com o botão de login.
          }
        }
        if (!cancelled) setStatus('success');
      } catch {
        if (!cancelled) setStatus('error');
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (status === 'loading') {
    return (
      <div className="auth-shell">
        <Card className="auth-card auth-card--wide">
          <Image width={140} src={logo} alt="Logo" preview={false} className="auth-logo" />
          <Spin size="large" tip="Confirmando seu e-mail..." />
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <Card className="auth-card auth-card--wide">
        <Image width={140} src={logo} alt="Logo Saldo Ativo" preview={false} className="auth-logo" />
        {status === 'success' && (
          <>
            <CheckCircleOutlined style={{ fontSize: 56, color: 'var(--color-success, #2F9E6F)', marginBottom: 16 }} />
            <Title level={4} className="auth-title">E-mail confirmado</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              Sua conta foi ativada. Entre com seu e-mail e senha para continuar.
            </Text>
            <Button
              type="primary"
              size="large"
              block
              onClick={() => navigate('/login', { state: { email: emailConfirmado } })}
            >
              Entrar
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16, textAlign: 'left' }}
              message="Este link expirou ou já foi usado"
              description="Se você já confirmou o e-mail, é só entrar. Se ainda não, peça um link novo abaixo."
            />
            <Button type="primary" size="large" block onClick={() => navigate('/login')} style={{ marginBottom: 20 }}>
              Entrar
            </Button>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, textAlign: 'left' }}>
              Ainda não confirmou? Receba um link novo:
            </Text>
            <ReenviarConfirmacao />
          </>
        )}
      </Card>
    </div>
  );
};

export default ConfirmarEmailPage;
